import * as S from "./EquipAnalysisContent.style";
import { LineChart } from "../../../atoms/Charts/LineChart";
import { InputSelect } from "../../../atoms/Inputs/InputSelect";
import { ScatterChart } from "../../../atoms/Charts/ScatterChart";
import { LineSectionChart } from "../../../atoms/Charts/LineSectionChart";
import { ContentBox } from "../../../molecules/ContentBox";
import { SelectOption } from "../../../../common/types/selectOption";
import { EquipmentDefaultValuesTypes } from "../../../../types/equipment";
import { ChangeEvent, FormEvent } from "react";
import { SearchBar } from "../../../molecules/SearchBar";
import { useQuery } from "@tanstack/react-query";
import { request } from "../../../../common/api";
import { TextInfo } from "../../../atoms/TextInfo";
import { Loading } from "../../../atoms/Loading";
import {
  GET_ALARM_STATISTIC_CHART_DATA,
  GET_SCATTER_ANALYSIS_CHART_DATA,
  GET_TIME_SERIES_ANALYSIS_CHART_DATA,
} from "../../../../common/query-keys";
import { InputDateHour } from "../../../atoms/Inputs/InputDateHour";
import dayjs from "dayjs";
import { Buttons } from "../../../atoms/Buttons";
import { beforeWeekTime } from "../../../../common/util/beforeWeek";

interface EquipAnalysisContentProps {
  search?: EquipmentDefaultValuesTypes;
  analysisSearch?: EquipmentDefaultValuesTypes;
  searchConfirm?: EquipmentDefaultValuesTypes;
  lowMachinerySelectOptions?: SelectOption[];
  sensorSelectOptions?: SelectOption[];
  handleLowMcySelectChange?: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  handleSensorChange?: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  setSearchData?: (name: string, value: string) => void;
  handleSearch: (e: FormEvent<HTMLFormElement>) => void;
  analysisDateChange?: (e: ChangeEvent<HTMLInputElement>) => void;
  analysisHandleDateChange?: (e: ChangeEvent<HTMLInputElement>) => void;
}

export const EquipAnalysisContent = ({
  search,
  searchConfirm,
  lowMachinerySelectOptions,
  sensorSelectOptions,
  handleLowMcySelectChange,
  handleSensorChange,
  setSearchData,
  handleSearch,
  analysisHandleDateChange,
}: EquipAnalysisContentProps) => {
  /**  Anomaly Score 차트
   * * fetch 코드 수정 완료 2025.05.23
   */
  const { data: alarmStatisticsChartData, isFetching: anomalyScoreLoading } =
    useQuery({
      queryKey: [GET_ALARM_STATISTIC_CHART_DATA, searchConfirm],
      queryFn: async () => {
        return await request<any>({
          method: "GET",
          url: `/equipment-monitoring/alarm-statistics-chart`,
          params: {
            steShiptypeNo: searchConfirm?.shipTypeNo,
            steShipNo: searchConfirm?.shipNo,
            steMcyName: searchConfirm?.mcyName,
            startDate: searchConfirm?.startDate,
            endDate: searchConfirm?.endDate,
            steLowMcyName: searchConfirm?.lowMcyName,
            steSensorCode: searchConfirm?.sensorCode,
          },
        }).then((res: any) => {
          return res.data;
        });
      },
      enabled: !!searchConfirm?.sensorCode,
    });

  /**  Time Series Analysis 차트
   * * fetch 코드 수정 완료 2025.05.23
   * ? 백엔드 메인 쿼리 확인 필요
   */
  const { data: timeSeriesChartData, isFetching: timeSeriesLoading } = useQuery(
    {
      queryKey: [GET_TIME_SERIES_ANALYSIS_CHART_DATA, searchConfirm],
      queryFn: async () => {
        return await request<any>({
          method: "GET",
          url: `/equipment-monitoring/time-series-analysis`,
          params: {
            steShiptypeNo: searchConfirm?.shipTypeNo,
            steShipNo: searchConfirm?.shipNo,
            steMcyName: searchConfirm?.mcyName,
            startDate: searchConfirm?.startDate,
            endDate: searchConfirm?.endDate,
            steLowMcyName: searchConfirm?.lowMcyName,
            steSensorCode: searchConfirm?.sensorCode,
          },
        }).then((res: any) => {
          return res.data;
        });
      },
      enabled: !!searchConfirm?.sensorCode,
    }
  );

  /**  Scatter Analysis 차트
   * * fetch 코드 수정 완료 2025.05.23
   * ? 백엔드 메인 쿼리 확인 필요
   */
  const { data: scatterAnalysisChartData, isFetching: scatterAnalysisLoading } =
    useQuery({
      queryKey: [GET_SCATTER_ANALYSIS_CHART_DATA, searchConfirm],
      queryFn: async () => {
        return await request<any>({
          method: "GET",
          url: `/equipment-monitoring/scatter-analysis`,
          params: {
            steShiptypeNo: searchConfirm?.shipTypeNo,
            steShipNo: searchConfirm?.shipNo,
            steMcyName: searchConfirm?.mcyName,
            startDate: searchConfirm?.startDate,
            endDate: searchConfirm?.endDate,
            steLowMcyName: searchConfirm?.lowMcyName,
            steSensorCode: searchConfirm?.sensorCode,
          },
        }).then((res: any) => {
          return res.data;
        });
      },
      enabled: !!searchConfirm?.sensorCode,
    });

  /** 상태진단 페이지 날짜 변경 핸들러 함수 */
  const analysisDateChange = (e: ChangeEvent<HTMLInputElement>) => {
    const startDate = e.target.value;
    const endDate = dayjs(e.target.value)
      .subtract(-7, "days")
      .format("YYYY-MM-DD HH:mm:ss");

    setSearchData?.("startDate", startDate);
    setSearchData?.("endDate", endDate);
  };

  return (
    <S.EquipAnalysisContent>
      <SearchBar
        onSearch={(e) => handleSearch(e)}
        searchInput={
          <>
            <InputSelect
              name="lowMcyName"
              size="md"
              placeholder="계통 선택"
              options={lowMachinerySelectOptions}
              width={120}
              onChange={handleLowMcySelectChange}
              value={search?.lowMcyName}
            />
            <InputSelect
              name="sensorCode"
              size="md"
              placeholder="센서 선택"
              options={sensorSelectOptions}
              width={220}
              onChange={handleSensorChange}
              value={search?.sensorCode}
            />
            <InputDateHour
              size="md"
              name="startDate"
              defaultValue={search?.startDate}
              value={search?.startDate}
              onChange={analysisDateChange}
            />
            <p>~</p>
            <InputDateHour
              size="md"
              startName="endDate"
              defaultValue={search?.endDate}
              value={search?.endDate}
              onChange={analysisHandleDateChange}
              // disabled={true}
            />
            <Buttons
              size="md"
              type="submit"
              layout="selectCondition"
              label="전1주"
              onClick={() => beforeWeekTime(search, setSearchData, 7)}
            />
          </>
        }
      />

      <S.EquipAnalysisBox>
        <ContentBox title="Scatter Analysis" shade={true} gridRow="1/9">
          {scatterAnalysisLoading ? (
            <Loading />
          ) : scatterAnalysisChartData ? (
            <ScatterChart chartData={scatterAnalysisChartData} />
          ) : (
            <>
              <TextInfo title="데이터가 없습니다." />
            </>
          )}
        </ContentBox>

        <ContentBox title="Time Series Analysis" shade={true} gridRow="1/5">
          {timeSeriesLoading ? (
            <Loading />
          ) : timeSeriesChartData ? (
            <LineChart chartData={timeSeriesChartData} />
          ) : (
            <>
              <TextInfo title="데이터가 없습니다." />
            </>
          )}
        </ContentBox>

        <ContentBox title="Anomaly Score" shade={true} gridRow="5/9">
          {anomalyScoreLoading ? (
            <Loading />
          ) : alarmStatisticsChartData ? (
            <LineSectionChart chartData={alarmStatisticsChartData?.result} />
          ) : (
            <>
              <TextInfo title="데이터가 없습니다." />
            </>
          )}
        </ContentBox>
      </S.EquipAnalysisBox>
    </S.EquipAnalysisContent>
  );
};
